// src/components/admin/payroll/AttendanceValidationWizard.js
import React, { useState, useMemo, useRef } from 'react';
import {
    ArrowLeft, X, ChevronRight, ChevronLeft, CheckCircle,
    AlertCircle, Calendar, Users, Clock, ChevronDown, ChevronUp,
    Maximize, Minimize, Filter, Search, CheckSquare, Square, Building
} from 'lucide-react';
import { getISTComponents, toISTDateString } from '../../../utils/dateHelpers';

// Helper to format date for display - handles timezone correctly
const formatDateForDisplay = (dateStr, options = { month: 'short', day: 'numeric' }) => {
    if (!dateStr) return '';
    // If it's already a Date object, use it directly
    if (dateStr instanceof Date) {
        return dateStr.toLocaleDateString('en-US', options);
    }
    // For date-only strings like "2024-01-15", parse components directly to avoid timezone shift
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const safeDate = new Date(year, month - 1, day, 12, 0, 0);
        return safeDate.toLocaleDateString('en-US', options);
    }
    // For ISO strings or other formats
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', options);
};

// Helper to parse date string safely to local timezone
const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    // For date-only strings, parse as local time
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    }
    // For ISO strings with time, convert to local
    return new Date(dateStr);
};

const AttendanceValidationWizard = ({
    validation,
    onClose,
    onComplete,
    theme = 'light',
    allEmployeesAttendance = {},
    employeeHolidayPlans = {},
    selectedMonth = '',
    employees = [],
    leaveRequests = []
}) => {
    const [currentStep, setCurrentStep] = useState(1); // 1: Partial Attendance, 2: Attendance & LOP Calculation, 3: Summary
    const [partialDayDecisions, setPartialDayDecisions] = useState({}); // Store decisions for each employee: 'hourly' or 'full'
    const [expandedEmployees, setExpandedEmployees] = useState({}); // Track which employees are expanded in Step 2
    const [isFullscreen, setIsFullscreen] = useState(false);
    const scrollContainerRef = useRef(null); // Ref to preserve scroll position

    // Bulk selection and filter states for partial day tab
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(new Set());
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        department: 'all',
        hoursRange: 'all', // 'all', 'below4', '4to6', '6to8'
        entity: 'all'
    });

    // Helper function to parse month/year from period string
    const getMonthYearFromPeriod = (monthStr) => {
        const [monthName, year] = monthStr.split(' ');
        const monthMap = {
            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };
        return { month: monthMap[monthName], year: parseInt(year) };
    };

    // Helper function to get detailed attendance breakdown for an employee
    const getEmployeeAttendanceBreakdown = (employeeId) => {
        if (!selectedMonth) return null;

        const { month, year } = getMonthYearFromPeriod(selectedMonth);
        const employee = employees.find(e => e.id === employeeId);
        if (!employee) return null;

        // Get holiday plan for this employee
        const employeeHolidayPlan = employeeHolidayPlans[employeeId];
        const allHolidays = employeeHolidayPlan?.holidays || [];

        // Calculate date range
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);
        const joiningDate = parseLocalDate(employee.joining_date || employee.joiningDate);
        const exitDate = parseLocalDate(employee.exit_date || employee.exitDate);

        const effectiveStartDate = (joiningDate && joiningDate > monthStart) ? joiningDate : monthStart;
        const effectiveEndDate = (exitDate && exitDate < monthEnd) ? exitDate : monthEnd;

        // Filter holidays for this month
        // FIX: Use toISTDateString for timezone-safe date comparison
        const effectiveStartStr = toISTDateString(effectiveStartDate);
        const effectiveEndStr = toISTDateString(effectiveEndDate);
        const monthHolidays = allHolidays.filter(holiday => {
            const holidayDateStr = toISTDateString(holiday.date);
            return holidayDateStr >= effectiveStartStr && holidayDateStr <= effectiveEndStr;
        }).map(h => ({
            date: h.date,
            name: h.name || h.holiday_name || 'Holiday'
        }));

        // Calculate weekly offs
        const weeklyOffs = [];
        const weeklyOffPolicy = employeeHolidayPlan?.weekly_off_policy || employee.weekly_off_policy || 'saturday-sunday';
        let offDays = [];
        if (weeklyOffPolicy.toLowerCase().includes('saturday') && weeklyOffPolicy.toLowerCase().includes('sunday')) {
            offDays = [0, 6]; // Sunday and Saturday
        } else if (weeklyOffPolicy.toLowerCase().includes('sunday')) {
            offDays = [0];
        } else if (weeklyOffPolicy.toLowerCase().includes('saturday')) {
            offDays = [6];
        }

        const currentDate = new Date(effectiveStartDate);
        while (currentDate <= effectiveEndDate) {
            // FIX: Use getISTComponents for timezone-safe day-of-week detection
            const { dayOfWeek } = getISTComponents(currentDate);
            if (offDays.includes(dayOfWeek)) {
                weeklyOffs.push({
                    date: new Date(currentDate)
                });
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Calculate total days
        const totalCalendarDays = Math.floor((effectiveEndDate - effectiveStartDate) / (1000 * 60 * 60 * 24)) + 1;

        return {
            holidays: monthHolidays,
            weeklyOffs: weeklyOffs,
            totalDaysInPeriod: totalCalendarDays
        };
    };

    // Get employees who took leaves
    const employeesWithLeaves = validation?.employees?.filter(emp => emp.leaves > 0) || [];

    // Get employees with partial days (< 8 hours)
    const employeesWithPartialDays = validation?.employees?.filter(emp => emp.partialDaysCount > 0) || [];

    // Get employees with LOP
    const employeesWithLOP = validation?.employees?.filter(emp => emp.lop > 0) || [];

    // Get employees with overtime (worked on holidays/weekoffs)
    const employeesWithOvertime = validation?.employees?.filter(emp => emp.overtime > 0) || [];

    // Debug log
    //console.log('🔍 Attendance Validation Wizard Data:');
    //console.log('  - Total employees:', validation?.employees?.length || 0);
    //console.log('  - Employees with leaves:', employeesWithLeaves.length);
    //console.log('  - Employees with PARTIAL DAYS:', employeesWithPartialDays.length);
    //console.log('  - Employees with LOP:', employeesWithLOP.length);
    if (employeesWithPartialDays.length > 0) {
        //console.log('  - Partial days employees:', employeesWithPartialDays.map(e => `${e.name} (${e.partialDaysCount} days)`));
    }

    // Calculate summary statistics
    const calculateSummary = () => {
        const allEmployees = validation?.employees || [];
        const totalEmployees = allEmployees.length;
        const totalLeaves = allEmployees.reduce((sum, emp) => sum + emp.leaves, 0);
        const totalLOP = allEmployees.reduce((sum, emp) => sum + emp.lop, 0);
        // FIX: Use emp.presentDays directly (correctly calculated in processEmployeeData)
        const totalPresent = allEmployees.reduce((sum, emp) => sum + (emp.presentDays || 0), 0);
        const totalWorkingDays = allEmployees.reduce((sum, emp) => sum + emp.workingDays, 0);
        const totalPartialDays = allEmployees.reduce((sum, emp) => sum + (emp.partialDaysCount || 0), 0);
        // Overtime stats (work on holidays/weekoffs)
        const totalOvertimeDays = allEmployees.reduce((sum, emp) => sum + (emp.overtime || 0), 0);
        const totalOvertimeHours = allEmployees.reduce((sum, emp) => sum + (emp.overtimeHours || 0), 0);

        return {
            totalEmployees,
            employeesWithLeaves: employeesWithLeaves.length,
            employeesWithLOP: employeesWithLOP.length,
            employeesWithPartialDays: employeesWithPartialDays.length,
            employeesWithOvertime: employeesWithOvertime.length,
            totalLeaves,
            totalLOP,
            totalPresent,
            totalWorkingDays,
            totalPartialDays,
            totalOvertimeDays,
            totalOvertimeHours,
            attendancePercentage: totalWorkingDays > 0 ? ((totalPresent / totalWorkingDays) * 100).toFixed(1) : 0
        };
    };

    const summary = calculateSummary();

    const handleNext = () => {
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () => {
        // Pass partial day decisions along with completion
        onComplete(partialDayDecisions);
        onClose();
    };

    const handlePartialDayDecision = (employeeId, decision) => {
        setPartialDayDecisions(prev => ({
            ...prev,
            [employeeId]: decision
        }));
    };

    const toggleEmployeeExpansion = (employeeId) => {
        // Save current scroll position before state update
        const scrollTop = scrollContainerRef.current?.scrollTop || 0;
        setExpandedEmployees(prev => ({
            ...prev,
            [employeeId]: !prev[employeeId]
        }));
        // Restore scroll position after render
        requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollTop;
            }
        });
    };

    // Get unique departments and entities from employees with partial days
    const filterOptions = useMemo(() => {
        const partialEmployees = validation?.employees?.filter(emp => emp.partialDaysCount > 0) || [];
        const departments = [...new Set(partialEmployees.map(emp => emp.department).filter(Boolean))];
        const entities = [...new Set(partialEmployees.map(emp => emp.entity || emp.business_unit || emp.company).filter(Boolean))];
        return { departments, entities };
    }, [validation?.employees]);

    // Filter and search employees with partial days
    const filteredPartialDayEmployees = useMemo(() => {
        let filtered = validation?.employees?.filter(emp => emp.partialDaysCount > 0) || [];

        // Apply search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(emp =>
                emp.name?.toLowerCase().includes(search) ||
                emp.employee_code?.toLowerCase().includes(search) ||
                emp.department?.toLowerCase().includes(search)
            );
        }

        // Apply department filter
        if (filters.department !== 'all') {
            filtered = filtered.filter(emp => emp.department === filters.department);
        }

        // Apply entity filter
        if (filters.entity !== 'all') {
            filtered = filtered.filter(emp =>
                (emp.entity || emp.business_unit || emp.company) === filters.entity
            );
        }

        // Apply hours range filter
        if (filters.hoursRange !== 'all') {
            filtered = filtered.filter(emp => {
                const avgHours = emp.partialDays?.length > 0
                    ? emp.partialDays.reduce((sum, day) => sum + parseFloat(day.workingHours || 0), 0) / emp.partialDays.length
                    : 0;

                switch (filters.hoursRange) {
                    case 'below4':
                        return avgHours < 4;
                    case '4to6':
                        return avgHours >= 4 && avgHours < 6;
                    case '6to8':
                        return avgHours >= 6 && avgHours < 8;
                    default:
                        return true;
                }
            });
        }

        return filtered;
    }, [validation?.employees, searchTerm, filters]);

    // Bulk selection handlers
    const handleSelectAll = () => {
        if (selectedEmployeeIds.size === filteredPartialDayEmployees.length) {
            setSelectedEmployeeIds(new Set());
        } else {
            setSelectedEmployeeIds(new Set(filteredPartialDayEmployees.map(emp => emp.id)));
        }
    };

    const handleSelectEmployee = (employeeId) => {
        setSelectedEmployeeIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeId)) {
                newSet.delete(employeeId);
            } else {
                newSet.add(employeeId);
            }
            return newSet;
        });
    };

    const handleBulkAssign = (decision) => {
        const newDecisions = { ...partialDayDecisions };
        selectedEmployeeIds.forEach(empId => {
            newDecisions[empId] = decision;
        });
        setPartialDayDecisions(newDecisions);
        // Clear selection after bulk assign
        setSelectedEmployeeIds(new Set());
    };

    const clearFilters = () => {
        setFilters({
            department: 'all',
            hoursRange: 'all',
            entity: 'all'
        });
        setSearchTerm('');
    };

    // Step indicator component
    const StepIndicator = () => {
        const steps = [
            { num: 1, label: 'Partial Attendance' },
            { num: 2, label: 'Attendance & LOP' },
            { num: 3, label: 'Summary' }
        ];

        return (
            <div className="flex items-center justify-center gap-1">
                {steps.map((step, index) => (
                    <div key={step.num} className="flex items-center">
                        <div className={`flex items-center justify-center w-7 h-7 rounded-full border-2 text-sm ${
                            step.num === currentStep
                                ? 'border-blue-600 bg-blue-50 text-blue-600'
                                : step.num < currentStep
                                ? 'border-green-600 bg-green-50 text-green-600'
                                : 'border-gray-300 bg-white text-gray-400'
                        }`}>
                            {step.num < currentStep ? (
                                <CheckCircle className="w-4 h-4" />
                            ) : (
                                <span className="font-semibold">{step.num}</span>
                            )}
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`w-6 h-0.5 mx-1 ${
                                step.num < currentStep ? 'bg-green-600' : 'bg-gray-300'
                            }`} />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // Tab 1: Partial Attendance with Bulk Selection and Filters
    const PartialAttendanceTab = () => {
        const allPartialDayEmployees = validation?.employees?.filter(emp => emp.partialDaysCount > 0) || [];
        const isAllSelected = filteredPartialDayEmployees.length > 0 && selectedEmployeeIds.size === filteredPartialDayEmployees.length;
        const isSomeSelected = selectedEmployeeIds.size > 0;
        const activeFiltersCount = [
            filters.department !== 'all',
            filters.hoursRange !== 'all',
            filters.entity !== 'all',
            searchTerm !== ''
        ].filter(Boolean).length;

        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3 mb-2">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100">
                        <Clock className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900">Partial Day Attendance</h3>
                        <p className="text-xs text-gray-600">Employees with &lt;8 hours - choose payment method</p>
                    </div>
                </div>

                {allPartialDayEmployees.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                        <p className="text-gray-600">No employees have partial day attendance this month</p>
                    </div>
                ) : (
                    <>
                        {/* Search and Filter Bar */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                {/* Search Input */}
                                <div className="flex-1 relative">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, code, or department..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                {/* Filter Toggle Button */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`px-4 py-2 border rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                                        showFilters || activeFiltersCount > 0
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <Filter className="w-4 h-4" />
                                    Filters
                                    {activeFiltersCount > 0 && (
                                        <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                                            {activeFiltersCount}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Filter Options */}
                            {showFilters && (
                                <div className="pt-3 border-t border-gray-200">
                                    <div className="grid grid-cols-3 gap-4">
                                        {/* Department Filter */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Department
                                            </label>
                                            <select
                                                value={filters.department}
                                                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="all">All Departments</option>
                                                {filterOptions.departments.map(dept => (
                                                    <option key={dept} value={dept}>{dept}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Hours Range Filter */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Avg Hours Worked
                                            </label>
                                            <select
                                                value={filters.hoursRange}
                                                onChange={(e) => setFilters(prev => ({ ...prev, hoursRange: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="all">All Hours</option>
                                                <option value="below4">Below 4 hours</option>
                                                <option value="4to6">4 to 6 hours</option>
                                                <option value="6to8">6 to 8 hours</option>
                                            </select>
                                        </div>

                                        {/* Entity Filter */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Entity / Business Unit
                                            </label>
                                            <select
                                                value={filters.entity}
                                                onChange={(e) => setFilters(prev => ({ ...prev, entity: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="all">All Entities</option>
                                                {filterOptions.entities.map(entity => (
                                                    <option key={entity} value={entity}>{entity}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {activeFiltersCount > 0 && (
                                        <div className="mt-3 flex justify-end">
                                            <button
                                                onClick={clearFilters}
                                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                            >
                                                Clear all filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Bulk Selection & Action Bar */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {/* Select All Checkbox */}
                                    <button
                                        onClick={handleSelectAll}
                                        className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                                    >
                                        {isAllSelected ? (
                                            <CheckSquare className="w-5 h-5 text-blue-600" />
                                        ) : (
                                            <Square className="w-5 h-5 text-gray-400" />
                                        )}
                                        <span className="font-medium">
                                            {isAllSelected ? 'Deselect All' : 'Select All'}
                                        </span>
                                    </button>

                                    {/* Selected Count */}
                                    {isSomeSelected && (
                                        <span className="text-sm text-gray-500">
                                            {selectedEmployeeIds.size} of {filteredPartialDayEmployees.length} selected
                                        </span>
                                    )}
                                </div>

                                {/* Bulk Action Buttons */}
                                {isSomeSelected && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 mr-2">Bulk assign:</span>
                                        <button
                                            onClick={() => handleBulkAssign('hourly')}
                                            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                                        >
                                            <Clock className="w-3.5 h-3.5" />
                                            Hourly Payment
                                        </button>
                                        <button
                                            onClick={() => handleBulkAssign('full')}
                                            className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                                        >
                                            <CheckCircle className="w-3.5 h-3.5" />
                                            Full Day Payment
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Summary Info */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                <div>
                                    <p className="font-semibold text-yellow-900 text-sm">
                                        {filteredPartialDayEmployees.length} employees showing
                                        {filteredPartialDayEmployees.length !== allPartialDayEmployees.length && (
                                            <span className="font-normal text-yellow-700">
                                                {' '}(filtered from {allPartialDayEmployees.length} total)
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-yellow-700 mt-1">
                                        Choose whether to pay proportionally based on hours worked or provide full day payment
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Employee List */}
                        {filteredPartialDayEmployees.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-600">No employees match the current filters</p>
                                <button
                                    onClick={clearFilters}
                                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Clear filters
                                </button>
                            </div>
                        ) : (
                            <div className="max-h-[400px] overflow-y-auto space-y-3">
                                {filteredPartialDayEmployees.map((employee) => {
                                    const decision = partialDayDecisions[employee.id] || 'hourly';
                                    const totalHours = employee.partialDays?.reduce((sum, day) => sum + parseFloat(day.workingHours || 0), 0) || 0;
                                    const isSelected = selectedEmployeeIds.has(employee.id);

                                    return (
                                        <div
                                            key={employee.id}
                                            className={`p-4 bg-white border-2 rounded-lg transition-colors ${
                                                isSelected
                                                    ? 'border-blue-400 bg-blue-50/30'
                                                    : 'border-yellow-200 hover:border-yellow-300'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {/* Selection Checkbox */}
                                                    <button
                                                        onClick={() => handleSelectEmployee(employee.id)}
                                                        className="flex-shrink-0"
                                                    >
                                                        {isSelected ? (
                                                            <CheckSquare className="w-5 h-5 text-blue-600" />
                                                        ) : (
                                                            <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                                                        )}
                                                    </button>

                                                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                                        <Users className="w-5 h-5 text-yellow-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">{employee.name}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {employee.department}
                                                            {(employee.entity || employee.business_unit) && (
                                                                <span className="ml-2 text-xs text-gray-400">
                                                                    • {employee.entity || employee.business_unit}
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-yellow-600">{employee.partialDaysCount}</p>
                                                    <p className="text-xs text-gray-500">Partial Days</p>
                                                </div>
                                            </div>

                                            {/* Partial Days Details */}
                                            <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                                                <p className="text-xs font-semibold text-gray-700 mb-2">Days with less than 8 hours:</p>
                                                <div className="space-y-1">
                                                    {employee.partialDays?.map((day, index) => (
                                                        <div key={index} className="flex items-center justify-between text-xs">
                                                            <span className="text-gray-600">{formatDateForDisplay(day.date)}</span>
                                                            <span className="font-medium text-gray-900">{day.workingHours} hours</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                                                    <span className="text-xs font-semibold text-gray-700">Total Hours:</span>
                                                    <span className="text-sm font-bold text-gray-900">{totalHours.toFixed(2)} hrs</span>
                                                </div>
                                            </div>

                                            {/* Decision Buttons */}
                                            <div className="space-y-2">
                                                <p className="text-xs font-semibold text-gray-700 mb-2">Payment Calculation:</p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        onClick={() => handlePartialDayDecision(employee.id, 'hourly')}
                                                        className={`p-3 rounded-lg border-2 transition-all ${
                                                            decision === 'hourly'
                                                                ? 'border-blue-600 bg-blue-50 text-blue-900'
                                                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <div className="text-center">
                                                            <p className="font-semibold text-sm">Calculate by Hours</p>
                                                            <p className="text-xs mt-1 opacity-75">Proportional payment</p>
                                                            {decision === 'hourly' && (
                                                                <CheckCircle className="w-4 h-4 mx-auto mt-1 text-blue-600" />
                                                            )}
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={() => handlePartialDayDecision(employee.id, 'full')}
                                                        className={`p-3 rounded-lg border-2 transition-all ${
                                                            decision === 'full'
                                                                ? 'border-green-600 bg-green-50 text-green-900'
                                                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <div className="text-center">
                                                            <p className="font-semibold text-sm">Full Day Payment</p>
                                                            <p className="text-xs mt-1 opacity-75">No deduction</p>
                                                            {decision === 'full' && (
                                                                <CheckCircle className="w-4 h-4 mx-auto mt-1 text-green-600" />
                                                            )}
                                                        </div>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Tab 2: Combined Attendance & LOP Calculation (Collapsible)
    const AttendanceAndLOPTab = () => {
        const allEmployees = validation?.employees || [];

        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3 mb-2">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                        <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900">Attendance & LOP Calculation</h3>
                        <p className="text-xs text-gray-600">Working days breakdown and LOP for all employees</p>
                    </div>
                </div>

                <div ref={scrollContainerRef} className="max-h-[calc(100vh-220px)] overflow-y-auto space-y-2">
                    {allEmployees.map((employee) => {
                        const isExpanded = expandedEmployees[employee.id];
                        const breakdown = getEmployeeAttendanceBreakdown(employee.id) || {};
                        const holidays = breakdown.holidays || [];
                        const weeklyOffs = breakdown.weeklyOffs || [];
                        // FIX: Use employee's effective period data (handles mid-month joiners/exits)
                        const totalDaysInPeriod = employee.totalDaysInPeriod || employee.workingDays || 30;
                        // FIX: Use employee.presentDays directly (correctly calculated in processEmployeeData)
                        const presentDays = employee.presentDays || 0;
                        // FIX: Use employee's calculated counts (correctly handles effective period for joiners/exits)
                        const holidayCount = employee.holidayCount || 0;
                        const weeklyOffCount = employee.weeklyOffCount || 0;

                        return (
                            <div
                                key={employee.id}
                                className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
                            >
                                {/* Collapsed View - Header */}
                                <div
                                    className="p-3 cursor-pointer hover:bg-gray-50"
                                    onClick={() => toggleEmployeeExpansion(employee.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                <Users className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900">{employee.name}</p>
                                                <p className="text-sm text-gray-500">{employee.department}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-6">
                                            {/* Total Payable Days */}
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-blue-600">{totalDaysInPeriod}</p>
                                                <p className="text-xs text-gray-500">Payable Days</p>
                                            </div>

                                            {/* LOP Days */}
                                            <div className="text-center">
                                                <p className={`text-2xl font-bold ${employee.lop > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {employee.lop}
                                                </p>
                                                <p className="text-xs text-gray-500">LOP Days</p>
                                            </div>

                                            {/* Expand/Collapse Icon */}
                                            <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                                                {isExpanded ? (
                                                    <ChevronUp className="w-5 h-5 text-gray-600" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-gray-600" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded View - Details */}
                                {isExpanded && (
                                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Left Column: Working Days Calculation */}
                                            <div className="space-y-3">
                                                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-blue-600" />
                                                    Working Days Calculation
                                                </h4>

                                                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                                    {/* Total Days in Month */}
                                                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-200">
                                                        <span className="text-sm font-semibold text-gray-900">Total Days in Month</span>
                                                        <span className="font-bold text-lg text-gray-900">{totalDaysInPeriod}</span>
                                                    </div>

                                                    {/* Breakdown (all payable) */}
                                                    <p className="text-xs text-gray-500 mb-2">Breakdown (All Payable):</p>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-sm text-gray-700">├─ Working Days</span>
                                                        <span className="font-semibold text-blue-600">{totalDaysInPeriod - holidayCount - weeklyOffCount}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-sm text-gray-700">├─ Holidays (Paid)</span>
                                                        <span className="font-semibold text-purple-600">{holidayCount}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-sm text-gray-700">├─ Weekly Offs (Paid)</span>
                                                        <span className="font-semibold text-orange-600">{weeklyOffCount}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-sm text-gray-700">└─ Approved Leaves (Paid)</span>
                                                        <span className="font-semibold text-green-600">{employee.leaves}</span>
                                                    </div>

                                                    <div className="flex items-center justify-between pt-2 border-t border-blue-300">
                                                        <span className="text-sm font-semibold text-gray-900">Total Payable Days</span>
                                                        <span className="font-bold text-lg text-blue-600">{totalDaysInPeriod}</span>
                                                    </div>
                                                </div>

                                                {/* LOP Calculation */}
                                                <div className={`border rounded p-3 ${employee.lop > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                                                    <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                        <AlertCircle className={`w-4 h-4 ${employee.lop > 0 ? 'text-red-600' : 'text-green-600'}`} />
                                                        LOP Calculation (Absent Days Only)
                                                    </h5>
                                                    <div className="space-y-1 text-xs">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Expected Working Days:</span>
                                                            <span className="font-semibold text-gray-900">{totalDaysInPeriod - holidayCount - weeklyOffCount}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Less: Present Days</span>
                                                            <span className="font-semibold text-green-600">-{presentDays}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Less: Approved Leaves</span>
                                                            <span className="font-semibold text-blue-600">-{employee.leaves}</span>
                                                        </div>
                                                        <div className="flex justify-between pt-1 border-t border-gray-300">
                                                            <span className="font-semibold text-gray-900">LOP (Absent Days):</span>
                                                            <span className={`font-bold text-lg ${employee.lop > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                {employee.lop}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Show specific LOP dates */}
                                                    {employee.lop > 0 && employee.lopDates && employee.lopDates.length > 0 && (
                                                        <div className="mt-2 pt-2 border-t border-red-200">
                                                            <p className="text-xs font-semibold text-red-800 mb-1">⚠️ LOP Days:</p>
                                                            <div className="space-y-1 ml-2">
                                                                {employee.lopDates.map((lopDay, idx) => (
                                                                    <p key={idx} className="text-xs text-red-700">
                                                                        • {new Date(lopDay.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ({lopDay.dayOfWeek}) - {lopDay.reason}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {employee.lop > 0 && (!employee.lopDates || employee.lopDates.length === 0) && (
                                                        <div className="mt-2 pt-2 border-t border-red-200">
                                                            <p className="text-xs text-red-700 italic">
                                                                ⚠️ Salary will be deducted proportionally for {employee.lop} day{employee.lop > 1 ? 's' : ''}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {employee.overtime > 0 && (
                                                        <div className="mt-2 pt-2 border-t border-cyan-200">
                                                            <div className="flex justify-between text-xs">
                                                                <span className="text-cyan-700">Overtime (Holiday/Weekoff):</span>
                                                                <span className="font-semibold text-cyan-600">{employee.overtime} day{employee.overtime > 1 ? 's' : ''} ({employee.overtimeHours?.toFixed(1) || 0}h)</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {employee.attendanceOnLeaveDays?.length > 0 && (
                                                        <div className="mt-2 pt-2 border-t border-amber-200">
                                                            <p className="text-xs text-amber-700 italic">
                                                                ℹ️ {employee.attendanceOnLeaveDays.length} day(s) had both attendance and leave - counted as leave
                                                            </p>
                                                        </div>
                                                    )}
                                                    {employee.leavesOnNonWorkingDays?.length > 0 && (
                                                        <div className="mt-2 pt-2 border-t border-gray-200">
                                                            <p className="text-xs text-gray-600 italic">
                                                                ℹ️ {employee.leavesOnNonWorkingDays.length} leave day(s) on holiday/weekoff - not counted
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Right Column: Holidays & Weekly Offs */}
                                            <div className="space-y-3">
                                                {/* Holidays List */}
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                                        Holidays ({holidays.length})
                                                    </h4>
                                                    {holidays.length > 0 ? (
                                                        <div className="bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto">
                                                            <div className="space-y-1">
                                                                {holidays.map((holiday, idx) => (
                                                                    <div key={idx} className="text-xs text-red-900 flex justify-between items-center p-1 hover:bg-red-100 rounded">
                                                                        <span className="font-medium">{holiday.name || 'Holiday'}</span>
                                                                        <span className="text-red-600 text-[10px]">
                                                                            {formatDateForDisplay(holiday.date)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-gray-500 italic bg-gray-100 p-2 rounded">No holidays in this period</p>
                                                    )}
                                                </div>

                                                {/* Weekly Offs */}
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                                        Weekly Offs ({weeklyOffs.length})
                                                    </h4>
                                                    {weeklyOffs.length > 0 ? (
                                                        <div className="bg-orange-50 border border-orange-200 rounded p-2 max-h-40 overflow-y-auto">
                                                            <div className="grid grid-cols-3 gap-1">
                                                                {weeklyOffs.slice(0, 15).map((weeklyOff, idx) => (
                                                                    <div key={idx} className="text-[10px] text-orange-900 text-center py-1 bg-white rounded font-medium">
                                                                        {formatDateForDisplay(weeklyOff.date)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {weeklyOffs.length > 15 && (
                                                                <p className="text-xs text-orange-600 text-center mt-1">
                                                                    +{weeklyOffs.length - 15} more
                                                                </p>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-gray-500 italic bg-gray-100 p-2 rounded">No weekly offs configured</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Tab 4: Summary
    const SummaryTab = () => (
        <div className="space-y-4">
            <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Attendance Summary</h3>
                <p className="text-sm text-gray-600">
                    Final overview of attendance, leaves, and LOP for this payroll period
                </p>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-blue-700 font-medium">Total Employees</p>
                            <p className="text-3xl font-bold text-blue-900 mt-1">{summary.totalEmployees}</p>
                        </div>
                        <Users className="w-10 h-10 text-blue-600 opacity-50" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-green-700 font-medium">Attendance Rate</p>
                            <p className="text-3xl font-bold text-green-900 mt-1">{summary.attendancePercentage}%</p>
                        </div>
                        <CheckCircle className="w-10 h-10 text-green-600 opacity-50" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-purple-700 font-medium">Employees on Leave</p>
                            <p className="text-3xl font-bold text-purple-900 mt-1">{summary.employeesWithLeaves}</p>
                            <p className="text-xs text-purple-600 mt-1">{summary.totalLeaves} days total</p>
                        </div>
                        <Calendar className="w-10 h-10 text-purple-600 opacity-50" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-orange-700 font-medium">Employees with LOP</p>
                            <p className="text-3xl font-bold text-orange-900 mt-1">{summary.employeesWithLOP}</p>
                            <p className="text-xs text-orange-600 mt-1">{summary.totalLOP} days total</p>
                        </div>
                        <AlertCircle className="w-10 h-10 text-orange-600 opacity-50" />
                    </div>
                </div>

                {summary.employeesWithPartialDays > 0 && (
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4 col-span-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-yellow-700 font-medium">Employees with Partial Days</p>
                                <p className="text-3xl font-bold text-yellow-900 mt-1">{summary.employeesWithPartialDays}</p>
                                <p className="text-xs text-yellow-600 mt-1">{summary.totalPartialDays} partial days • Payment calculated based on selected method</p>
                            </div>
                            <Clock className="w-10 h-10 text-yellow-600 opacity-50" />
                        </div>
                    </div>
                )}

                {/* Overtime Section - Work on Holidays/Weekoffs */}
                {summary.employeesWithOvertime > 0 && (
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-lg p-4 col-span-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-cyan-700 font-medium">Employees with Overtime</p>
                                <p className="text-3xl font-bold text-cyan-900 mt-1">{summary.employeesWithOvertime}</p>
                                <p className="text-xs text-cyan-600 mt-1">{summary.totalOvertimeDays} days ({summary.totalOvertimeHours?.toFixed(1) || 0}h) worked on holidays/weekoffs</p>
                            </div>
                            <Clock className="w-10 h-10 text-cyan-600 opacity-50" />
                        </div>
                    </div>
                )}
            </div>

            {/* Detailed Breakdown */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-600" />
                    Attendance Breakdown
                </h4>
                <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Total Working Days</span>
                        <span className="font-semibold text-gray-900">{summary.totalWorkingDays}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Total Present Days</span>
                        <span className="font-semibold text-green-600">{summary.totalPresent}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Total Leave Days</span>
                        <span className="font-semibold text-purple-600">{summary.totalLeaves}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Total LOP Days</span>
                        <span className="font-semibold text-orange-600">{summary.totalLOP}</span>
                    </div>
                    {summary.totalOvertimeDays > 0 && (
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-600">Total Overtime (Holiday/Weekoff)</span>
                            <span className="font-semibold text-cyan-600">{summary.totalOvertimeDays} days ({summary.totalOvertimeHours?.toFixed(1) || 0}h)</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Warning for LOP if any */}
            {summary.employeesWithLOP > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                            <p className="font-semibold text-orange-900 text-sm">Salary Deduction Required</p>
                            <p className="text-xs text-orange-700 mt-1">
                                {summary.employeesWithLOP} employees have LOP. Their salaries will be calculated proportionally based on present days.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] ${
            isFullscreen ? 'p-0' : 'p-4'
        }`}>
            <div className={`bg-white shadow-2xl w-full flex flex-col transition-all duration-300 ${
                isFullscreen ? 'max-w-full h-full m-0 rounded-none' : 'max-w-6xl max-h-[90vh] rounded-xl'
            }`}>
                {/* Header - Compact */}
                <div className="px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4 text-gray-600" />
                            </button>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">
                                    Attendance Validation
                                </h2>
                                <p className="text-xs text-gray-600">
                                    Step {currentStep}/3 • {validation?.totalEmployees || 0} employees
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <StepIndicator />
                            <button
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                            >
                                {isFullscreen ? (
                                    <Minimize className="w-4 h-4 text-gray-600" />
                                ) : (
                                    <Maximize className="w-4 h-4 text-gray-600" />
                                )}
                            </button>
                            <button
                                onClick={onClose}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Step Content */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                    {currentStep === 1 && <PartialAttendanceTab />}
                    {currentStep === 2 && <AttendanceAndLOPTab />}
                    {currentStep === 3 && <SummaryTab />}
                </div>

                {/* Footer - Compact */}
                <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            className={`px-3 py-1.5 text-sm border rounded-lg font-medium transition-colors flex items-center gap-1 ${
                                currentStep === 1
                                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'border-gray-300 text-gray-700 hover:bg-white'
                            }`}
                        >
                            <ChevronLeft className="w-3 h-3" />
                            Back
                        </button>

                        {currentStep < 3 ? (
                            <button
                                onClick={handleNext}
                                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                                Continue
                                <ChevronRight className="w-3 h-3" />
                            </button>
                        ) : (
                            <button
                                onClick={handleComplete}
                                className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-1"
                            >
                                <CheckCircle className="w-3 h-3" />
                                Complete Validation
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttendanceValidationWizard;
